// PDF generator — Devis / Facture / Reçu, template VTC (modèles utilisateur)
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DOC_LABEL = { devis: "DEVIS", facture: "FACTURE", recu: "REÇU" };

function fmt(v) {
  return Number(v || 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtNum(v) {
  return Number(v || 0).toLocaleString("fr-FR");
}

function labelValue(doc, x, y, label, value, opts = {}) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 40, 60);
  doc.text(label, x, y);
  const labelWidth = doc.getTextWidth(label + "  ");
  doc.setFont("helvetica", "normal");
  doc.setTextColor(50, 60, 80);
  doc.text(String(value ?? ""), x + labelWidth, y, opts);
}

function checkbox(doc, x, y, size, checked) {
  doc.setDrawColor(30, 40, 60);
  doc.setLineWidth(0.6);
  doc.rect(x, y - size + 1.5, size, size);
  if (checked) {
    doc.setLineWidth(1.2);
    doc.line(x + 1.2, y - size / 2 + 1, x + size / 2, y - 0.5);
    doc.line(x + size / 2, y - 0.5, x + size - 0.5, y - size + 2);
    doc.setLineWidth(0.6);
  }
}

export function generateDocumentPDF({ document, client, settings }) {
  const type = document.type;
  const label = DOC_LABEL[type] || "DOCUMENT";
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // ============================================================
  // HEADER — Company (left) + Client (right)
  // ============================================================
  const headerTop = margin + 8;

  // Left: Company
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 40, 60);
  let leftY = headerTop;
  const compLines = [
    ["Nom de la société :", settings?.company_name || ""],
    ["Adresse de la société :", settings?.address || ""],
    ["Code postale, Ville :", settings?.city || ""],
    ["SIRET :", settings?.siret || ""],
    ["Tél :", settings?.phone || ""],
  ];
  compLines.forEach(([l, v]) => {
    labelValue(doc, margin, leftY, l, v);
    leftY += 14;
  });

  // Right: Client
  const rightX = pageW / 2 + 20;
  let rightY = headerTop;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 40, 60);
  doc.text("Coordonnées du client", rightX, rightY);
  rightY += 14;
  const cliLines = [
    ["Nom :", `${client?.prenom || ""} ${client?.nom || ""}`.trim()],
    ["Société :", client?.societe || ""],
    ["Adresse :", client?.adresse || ""],
    ["Tél :", client?.telephone || ""],
  ];
  cliLines.forEach(([l, v]) => {
    labelValue(doc, rightX, rightY, l, v);
    rightY += 14;
  });

  const headerBottom = Math.max(leftY, rightY) + 6;
  doc.setDrawColor(200, 210, 225);
  doc.setLineWidth(0.5);
  doc.line(margin, headerBottom, pageW - margin, headerBottom);

  // ============================================================
  // TITLE
  // ============================================================
  const titleY = headerBottom + 36;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(20, 30, 50);
  doc.text(label, pageW / 2, titleY, { align: "center" });

  // ============================================================
  // N° + Date (small table)
  // ============================================================
  const infoY = titleY + 24;
  const boxW = 220;
  const boxH = 44;
  const boxX = margin;
  doc.setDrawColor(30, 40, 60);
  doc.setLineWidth(0.6);
  doc.rect(boxX, infoY, boxW, boxH);
  doc.line(boxX + boxW / 2, infoY, boxX + boxW / 2, infoY + boxH);
  doc.line(boxX, infoY + boxH / 2, boxX + boxW, infoY + boxH / 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 40, 60);
  const numeroLabel = type === "devis" ? "N° de devis" : type === "facture" ? "N° de facture" : "N° de reçu";
  const dateLabel = type === "devis" ? "Date du devis" : type === "facture" ? "Date de la facture" : "Date du reçu";
  doc.text(numeroLabel, boxX + 8, infoY + 15);
  doc.text(dateLabel, boxX + boxW / 2 + 8, infoY + 15);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(document.numero || "—", boxX + 8, infoY + 35);
  doc.text(document.date || "—", boxX + boxW / 2 + 8, infoY + 35);

  let cursorY = infoY + boxH + 20;

  // ============================================================
  // Réservation / Prise en charge (devis + facture VTC)
  // ============================================================
  const showTransport = !!(
    document.reservation_date ||
    document.reservation_time ||
    document.pickup_date ||
    document.pickup_time ||
    document.pickup_address ||
    document.dropoff_address
  );

  if (showTransport) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30, 40, 60);

    const col1X = margin;
    const col2X = pageW / 2 + 20;
    labelValue(doc, col1X, cursorY, "Date de réservation :", document.reservation_date || "");
    labelValue(doc, col2X, cursorY, "Heure de réservation :", document.reservation_time || "");
    cursorY += 16;
    labelValue(doc, col1X, cursorY, "Date de prise en charge :", document.pickup_date || "");
    labelValue(doc, col2X, cursorY, "Heure de prise en charge :", document.pickup_time || "");
    cursorY += 16;
    labelValue(doc, col1X, cursorY, "Adresse de prise en charge :", document.pickup_address || "");
    cursorY += 16;
    labelValue(doc, col1X, cursorY, "Adresse de dépose :", document.dropoff_address || "");
    cursorY += 20;
  }

  // ============================================================
  // Type de prestation (checkboxes)
  // ============================================================
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 40, 60);
  doc.text("Type de prestation :", margin, cursorY);
  cursorY += 16;

  const cbSize = 10;
  const isTransfert = document.service_type === "transfert";
  const isMAD = document.service_type === "mise_a_disposition";
  checkbox(doc, margin + 6, cursorY, cbSize, isTransfert);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Transfert (A → B)", margin + 22, cursorY);
  checkbox(doc, pageW / 2 + 20, cursorY, cbSize, isMAD);
  doc.text("Mise à disposition", pageW / 2 + 36, cursorY);
  cursorY += 20;

  // ============================================================
  // Lines table
  // ============================================================
  const rows = (document.lines || []).length
    ? document.lines.map((l) => [
        l.description || "",
        fmt(l.unit_price),
        fmtNum(l.quantity),
        fmt(Number(l.quantity || 0) * Number(l.unit_price || 0)),
      ])
    : [["—", "", "", ""]];

  autoTable(doc, {
    startY: cursorY,
    head: [["Désignation", "Prix unitaire HT", "Quantité", "Prix total HT"]],
    body: rows,
    theme: "grid",
    headStyles: {
      fillColor: [30, 40, 60],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9,
      halign: "center",
    },
    bodyStyles: {
      fontSize: 10,
      textColor: [30, 40, 60],
      lineColor: [180, 190, 210],
    },
    styles: { cellPadding: 6, lineWidth: 0.4 },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { halign: "right", cellWidth: 90 },
      2: { halign: "right", cellWidth: 60 },
      3: { halign: "right", cellWidth: 90 },
    },
    margin: { left: margin, right: margin },
  });

  const afterTableY = doc.lastAutoTable.finalY + 12;

  // ============================================================
  // Totals block (right-aligned, boxed like template)
  // ============================================================
  const totalsBoxW = 260;
  const totalsBoxH = 66;
  const totalsX = pageW - margin - totalsBoxW;
  const totalsY = afterTableY;
  doc.setDrawColor(30, 40, 60);
  doc.setLineWidth(0.6);
  doc.rect(totalsX, totalsY, totalsBoxW, totalsBoxH);
  doc.line(totalsX + totalsBoxW * 0.55, totalsY, totalsX + totalsBoxW * 0.55, totalsY + totalsBoxH);
  doc.line(totalsX, totalsY + totalsBoxH / 3, totalsX + totalsBoxW, totalsY + totalsBoxH / 3);
  doc.line(totalsX, totalsY + (2 * totalsBoxH) / 3, totalsX + totalsBoxW, totalsY + (2 * totalsBoxH) / 3);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 40, 60);
  const lblX = totalsX + 8;
  const valX = totalsX + totalsBoxW - 8;
  const row1Y = totalsY + totalsBoxH / 6 + 4;
  const row2Y = totalsY + totalsBoxH / 2 + 4;
  const row3Y = totalsY + (5 * totalsBoxH) / 6 + 4;
  doc.text("% TVA", lblX, row1Y);
  doc.text("Montant TVA", lblX, row2Y);
  doc.text("Total TTC", lblX, row3Y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const tvaRate = Number(document.tva_rate || 0);
  const totalHt = Number(document.total_ht ?? document.subtotal ?? 0);
  const tvaAmount = Number(document.tva_amount ?? (totalHt * tvaRate) / 100);
  const totalTtc = Number(document.total_ttc ?? document.total ?? totalHt + tvaAmount);
  doc.text(`${tvaRate.toLocaleString("fr-FR")} %`, valX, row1Y, { align: "right" });
  doc.text(fmt(tvaAmount), valX, row2Y, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(fmt(totalTtc), valX, row3Y, { align: "right" });

  // Subtotal HT à gauche des totaux, si remise appliquée
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 90, 110);
  let subY = totalsY;
  doc.text(`Sous-total HT : ${fmt(document.subtotal)}`, margin, subY + 12);
  if (document.discount) {
    doc.text(`Remise : - ${fmt(document.discount)}`, margin, subY + 26);
    doc.text(`Total HT : ${fmt(totalHt)}`, margin, subY + 40);
  } else {
    doc.text(`Total HT : ${fmt(totalHt)}`, margin, subY + 26);
  }

  let footerY = totalsY + totalsBoxH + 24;

  // Mention TVA
  if (tvaRate === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(80, 90, 110);
    doc.text("TVA non applicable, art. 293 B du CGI", margin, footerY);
    footerY += 16;
  }

  // Notes
  if (document.notes) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 40, 60);
    doc.text("Notes", margin, footerY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(70, 80, 100);
    const noteLines = doc.splitTextToSize(document.notes, pageW - 2 * margin);
    doc.text(noteLines, margin, footerY + 14);
    footerY += 14 + noteLines.length * 12 + 6;
  }

  // Reçu : mention paiement
  if (type === "recu") {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(30, 40, 60);
    const paid = document.payment_method ? ` par ${document.payment_method}` : "";
    doc.text(`Reçu la somme de ${fmt(totalTtc)}${paid}.`, margin, footerY);
    footerY += 20;
  }

  // Facture : mention retard paiement
  if (type === "facture") {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 130, 150);
    const t = "En cas de retard de paiement, une indemnité forfaitaire de 40 € pour frais de recouvrement sera exigible (art. L441-6 du Code de commerce). Aucun escompte pour paiement anticipé.";
    const lines = doc.splitTextToSize(t, pageW - 2 * margin);
    doc.text(lines, margin, footerY);
    footerY += lines.length * 10 + 6;
  }

  // Devis : signature client (bas droite)
  if (type === "devis") {
    const signY = Math.max(footerY + 30, pageH - 110);
    doc.setDrawColor(30, 40, 60);
    doc.setLineWidth(0.5);
    doc.rect(pageW - margin - 200, signY, 200, 70);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 40, 60);
    doc.text('Signature du client (précédée de "Bon pour accord")', pageW - margin - 195, signY + 14);
  }

  const filename = `${document.numero || label}-${(client?.nom || "client").replace(/\s+/g, "_")}.pdf`;
  doc.save(filename);
}
